import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { consolidatedBusinessApi } from '../../services/consolidatedBusinessApi';
import type { MailHistoryItem } from '../../types/consBusinessMail';
import { fmtDate } from './format';
import { SortableHeader, type SortState } from '../ui/SortableHeader';
import { useTableSort } from '../../hooks/useTableSort';
import { useResizableColumns } from '../../hooks/useResizableColumns';

interface Props {
    groupKey: string;
    /** Bumped by the drawer after a send so the list picks the new mail up. */
    refreshKey: number;
}

type MailSortKey = 'sentAt' | 'kind' | 'sentByEmail' | 'recipients' | 'subject' | 'status';

const COLUMNS: { key: MailSortKey; label: string; width: number }[] = [
    { key: 'sentAt', label: 'Date', width: 90 },
    { key: 'kind', label: 'Type', width: 80 },
    { key: 'sentByEmail', label: 'Sender', width: 160 },
    { key: 'recipients', label: 'Recipients', width: 200 },
    { key: 'subject', label: 'Subject', width: 240 },
    { key: 'status', label: 'Status', width: 80 },
];

/**
 * "Sent e-mails" — the mails that covered THIS combination: internal forwards, and the
 * customer outreach mails sent before that path was removed on 2026-10-07. The two are
 * labelled, because "a colleague was told" and "the customer was written to" are different
 * answers to the question below.
 *
 * It shows everyone's mails, not just the viewer's, and it shows FAILED sends in red. Both
 * are the point: the question a rep asks before writing is "has anybody already approached
 * this customer about this product", and the two ways to get that answer wrong are hiding a
 * colleague's mail and hiding an attempt that errored.
 *
 * The house table since the design audit of 2026-10-07 (S2-03, the same fix Customer 360 made in
 * DU5): sticky, sortable, resizable and bounded, so a combination with many mails cannot push the
 * rest of the drawer off the screen. It opens newest first.
 */
export function ConsBusinessMailHistory({ groupKey, refreshKey }: Props) {
    const [items, setItems] = useState<MailHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        consolidatedBusinessApi.mailHistory(groupKey)
            .then(r => { if (!cancelled) setItems(r.items ?? []); })
            .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load e-mails'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [groupKey, refreshKey]);

    const accessors = useMemo(() => ({
        sentAt: (m: MailHistoryItem) => m.sentAt,
        kind: (m: MailHistoryItem) => m.kind,
        sentByEmail: (m: MailHistoryItem) => m.sentByEmail,
        recipients: (m: MailHistoryItem) => m.to.join(', '),
        subject: (m: MailHistoryItem) => m.subject,
        // Failures first when descending: the row somebody has to do something about.
        status: (m: MailHistoryItem) => (m.success ? 1 : 0),
    }), []);

    const { sorted, sort, toggleSort } = useTableSort(items, accessors, { key: 'sentAt', dir: 'desc' });
    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, 'consbusiness-mailhistory-col-widths');

    const headerSort: SortState<MailSortKey> = { key: sort.key as MailSortKey | null, direction: sort.dir };
    const onSort = (key: MailSortKey) => {
        if (shouldSuppressClick()) return;
        toggleSort(key);
    };

    if (loading) {
        return (
            <p className="flex items-center gap-1.5 text-xs text-gray-500">
                <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" /> Loading e-mails…
            </p>
        );
    }
    if (error) return <p role="alert" className="text-xs text-red-600">{error}</p>;
    if (items.length === 0) return <p className="text-xs text-gray-500">No e-mails sent yet.</p>;

    return (
        <div className="max-h-64 overflow-auto rounded-lg border border-gray-200">
            <table className="w-full table-fixed text-xs">
                <colgroup>
                    {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                </colgroup>
                <thead>
                    <tr>
                        {COLUMNS.map(c => (
                            <SortableHeader
                                key={c.key}
                                label={c.label}
                                sortKey={c.key}
                                sort={headerSort}
                                onSort={onSort}
                                onResizeStart={x => startResize(c.key, x)}
                                onResetWidth={() => resetWidth(c.key)}
                                isResizing={resizingKey === c.key}
                            />
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {sorted.map(m => (
                        <tr key={m.emailLogId} className="align-top hover:bg-gray-50">
                            <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-gray-600">{fmtDate(m.sentAt)}</td>
                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">
                                {m.kind === 'internal' ? 'Internal' : 'Customer'}
                            </td>
                            <td className="truncate px-3 py-1.5 text-gray-700" title={m.sentByEmail}>{m.sentByEmail}</td>
                            <td className="truncate px-3 py-1.5 text-gray-600" title={[...m.to, ...m.cc].join(', ')}>
                                {m.to.join(', ')}
                                {m.cc.length > 0 && (
                                    <span className="text-gray-500"> · cc {m.cc.join(', ')}</span>
                                )}
                            </td>
                            <td className="truncate px-3 py-1.5 text-gray-900" title={m.subject}>
                                {m.subject}
                                {/* A mail may cover several combinations (D9) — say so, so nobody
                                    reads it as a mail about this product alone. */}
                                {m.groupCount > 1 && (
                                    <span
                                        className="ml-1.5 inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600"
                                        title={`This mail covered ${m.groupCount} combinations`}
                                    >
                                        {m.groupCount}×
                                    </span>
                                )}
                            </td>
                            <td className="px-3 py-1.5">
                                {m.success ? (
                                    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                                        Sent
                                    </span>
                                ) : (
                                    <span
                                        className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700"
                                        title={m.errorMessage ?? 'Send failed'}
                                    >
                                        <AlertTriangle aria-hidden="true" className="h-3 w-3" /> Failed
                                    </span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
